import { Link } from "react-router-dom";
import { Building2, MessageSquare, DollarSign, Activity, AlertCircle } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { tenants } from "@/data/mockData";

export default function AdminDashboard() {
  return (
    <div className="space-y-6">
      <PageHeader title="Platform admin" description="Cross-tenant health, alerts and revenue overview." />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Total tenants", value: "184", icon: Building2 },
          { label: "Messages today", value: "1.42M", icon: MessageSquare },
          { label: "Active channels", value: "623", icon: Activity },
          { label: "Revenue MTD", value: "$48,210", icon: DollarSign },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{s.label}</span>
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary"><s.icon className="h-4 w-4" /></div>
            </div>
            <div className="mt-3 text-2xl font-semibold tracking-tight">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-sm font-semibold">Tenant health</h2>
            <Link to="/admin/tenants" className="text-xs font-medium text-primary hover:underline">View all tenants</Link>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-5 py-3 text-left font-medium">Tenant</th><th className="px-5 py-3 text-left font-medium">Status</th><th className="px-5 py-3 text-right font-medium">Balance</th><th className="px-5 py-3 text-left font-medium">Last activity</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {tenants.map((t) => (
                <tr key={t.id} className="hover:bg-muted/40">
                  <td className="px-5 py-3 font-medium">{t.company}</td>
                  <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                  <td className="px-5 py-3 text-right">${t.balance.toLocaleString()}</td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{t.lastActivity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-5 py-4"><h2 className="text-sm font-semibold">System alerts</h2></div>
          <ul className="divide-y divide-border">
            {[
              { sev: "danger", msg: "Webhook delivery failing for tenant Bluefin Logistics (12 retries)" },
              { sev: "warning", msg: "Queue backlog: 24,000 messages pending on shard-3" },
              { sev: "warning", msg: "Rate-limit hit for Sunset Cosmetics (Marketing tier)" },
              { sev: "info", msg: "Scheduled maintenance window starts in 4 hours" },
            ].map((a, i) => (
              <li key={i} className="flex items-start gap-3 px-5 py-3 text-xs">
                <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${a.sev === "danger" ? "text-destructive" : a.sev === "warning" ? "text-warning-foreground" : "text-info"}`} />
                <span>{a.msg}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
